'use client';

import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  FileFormat,
  IngestionSourceType,
  KafkaKeyHandling,
  KafkaMessageFormat,
  KafkaStartingOffset,
  Provider,
} from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { Regions } from '@/data/inventory/regions';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';
import { getStepperLabels } from '../kafkaHelpers';

interface RegionOption {
  value: string;
  label: string;
}

const AWS_REGION_OPTIONS: RegionOption[] = Object.entries(
  Regions[Provider.Aws]
).map(([code, { displayName }]) => ({
  value: code,
  label: `${code} - ${displayName}`,
}));

const GCS_LOCATION_OPTIONS: RegionOption[] = Object.entries(
  Regions[Provider.Gcp]
).map(([code, { displayName }]) => ({
  value: code,
  label: `${code} - ${displayName}`,
}));

const BOOTSTRAP_SERVERS_REGEX = /^[\w.-]+:\d{1,5}(\s*,\s*[\w.-]+:\d{1,5})*$/;

const BOOTSTRAP_SERVERS_VALIDATION: FieldValidation = {
  validateInput: (value: string) =>
    value.length === 0 || BOOTSTRAP_SERVERS_REGEX.test(value.trim()),
  errorMessage: () =>
    'Must be comma-separated host:port entries, e.g. broker1:9092, broker2:9092.',
};

const BUCKET_NAME_MAX_LENGTH = 63;

const BUCKET_NAME_VALIDATION: FieldValidation = {
  validateInput: (value: string) =>
    value.length === 0 || value.trim().length <= BUCKET_NAME_MAX_LENGTH,
  errorMessage: () =>
    `Bucket name must be ${BUCKET_NAME_MAX_LENGTH} characters or fewer.`,
};

const {
  validation: ACCOUNT_ID_VALIDATION,
  placeholder: ACCOUNT_ID_PLACEHOLDER,
} = getCloudFieldConfig(Provider.Aws, 'accountId', { required: true });

export function SourceStep(props: StepProps<IngestionPipelineWizardState>) {
  const {
    sourceType,
    s3Bucket,
    s3Region,
    s3OwnerAccountId,
    gcsBucket,
    gcsProjectId,
    gcsLocation,
    fileFormat,
    kafkaBootstrapServers,
    kafkaTopic,
    kafkaStartingOffset,
    kafkaMessageFormat,
    kafkaKeyHandling,
    kafkaConsumerGroupId,
  } = props.wizardState;

  const isS3 = sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_S3;
  const isKafka =
    sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_KAFKA;

  const selectedAwsRegion = useMemo(
    () => AWS_REGION_OPTIONS.find((o) => o.value === s3Region) ?? null,
    [s3Region]
  );
  const selectedGcsLocation = useMemo(
    () => GCS_LOCATION_OPTIONS.find((o) => o.value === gcsLocation) ?? null,
    [gcsLocation]
  );

  const canGoNext = () => {
    if (isKafka) {
      return (
        kafkaBootstrapServers.trim() !== '' &&
        BOOTSTRAP_SERVERS_VALIDATION.validateInput(kafkaBootstrapServers) &&
        kafkaTopic.trim() !== ''
      );
    }
    if (isS3) {
      return (
        s3Bucket.trim() !== '' &&
        s3Bucket.trim().length <= BUCKET_NAME_MAX_LENGTH &&
        s3Region.trim() !== '' &&
        ACCOUNT_ID_VALIDATION.validateInput(s3OwnerAccountId)
      );
    }
    return (
      gcsBucket.trim() !== '' &&
      gcsBucket.trim().length <= BUCKET_NAME_MAX_LENGTH &&
      gcsProjectId.trim() !== '' &&
      gcsLocation.trim() !== ''
    );
  };

  return (
    <StepContainer
      stepperIndex={1}
      stepperLabels={getStepperLabels(sourceType)}
      canGoNext={canGoNext}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      onBackClick={props.back}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* S3 Source Properties */}
        {isS3 && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>S3 Source Properties</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Configure the S3 bucket and credentials for data ingestion.
                </Typography>
              </Stack>

              <FormTextField
                title='Bucket'
                placeholder='e.g. my-bucket'
                value={s3Bucket}
                validation={BUCKET_NAME_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    s3Bucket: e.target.value,
                  }))
                }
                dataTestId='s3-bucket-input'
              />

              <FormControl fullWidth>
                <FormLabel>Region</FormLabel>
                <Autocomplete
                  options={AWS_REGION_OPTIONS}
                  value={selectedAwsRegion}
                  onChange={(_event, newValue) =>
                    props.setWizardState((s) => ({
                      ...s,
                      s3Region: newValue?.value ?? '',
                    }))
                  }
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      placeholder='Search regions…'
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          'data-testid': 's3-region-input',
                        },
                      }}
                    />
                  )}
                />
              </FormControl>

              <FormTextField
                title='Owner Account ID'
                placeholder={ACCOUNT_ID_PLACEHOLDER}
                value={s3OwnerAccountId}
                validation={ACCOUNT_ID_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    s3OwnerAccountId: e.target.value,
                  }))
                }
                dataTestId='s3-owner-account-input'
              />
            </Stack>
          </Box>
        )}

        {/* Kafka Source Properties */}
        {isKafka && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>Kafka Source Properties</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Configure the Kafka cluster and topic for data ingestion.
                </Typography>
              </Stack>

              <FormTextField
                title='Bootstrap Servers'
                placeholder='e.g. broker1:9092, broker2:9092'
                value={kafkaBootstrapServers}
                validation={BOOTSTRAP_SERVERS_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaBootstrapServers: e.target.value,
                  }))
                }
                dataTestId='kafka-bootstrap-servers-input'
              />

              <FormTextField
                title='Topic'
                placeholder='e.g. my-topic'
                value={kafkaTopic}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaTopic: e.target.value,
                  }))
                }
                dataTestId='kafka-topic-input'
              />

              <FormTextField
                title='Consumer Group ID'
                placeholder='Optional'
                value={kafkaConsumerGroupId}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaConsumerGroupId: e.target.value,
                  }))
                }
                dataTestId='kafka-consumer-group-input'
              />

              <FormControl fullWidth>
                <FormLabel>Starting Offset</FormLabel>
                <Select
                  size='small'
                  value={kafkaStartingOffset}
                  onChange={(e) => {
                    const val = Object.values(KafkaStartingOffset).find(
                      (v) => v === e.target.value
                    );
                    if (val) {
                      props.setWizardState((s) => ({
                        ...s,
                        kafkaStartingOffset: val,
                      }));
                    }
                  }}
                >
                  <MenuItem
                    value={KafkaStartingOffset.KAFKA_STARTING_OFFSET_EARLIEST}
                  >
                    Earliest
                  </MenuItem>
                  <MenuItem
                    value={KafkaStartingOffset.KAFKA_STARTING_OFFSET_LATEST}
                  >
                    Latest
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>Message Format</FormLabel>
                <Select
                  size='small'
                  value={kafkaMessageFormat}
                  onChange={(e) => {
                    const val = Object.values(KafkaMessageFormat).find(
                      (v) => v === e.target.value
                    );
                    if (val) {
                      props.setWizardState((s) => ({
                        ...s,
                        kafkaMessageFormat: val,
                      }));
                    }
                  }}
                >
                  <MenuItem
                    value={KafkaMessageFormat.KAFKA_MESSAGE_FORMAT_AVRO}
                  >
                    Avro
                  </MenuItem>
                  <MenuItem
                    value={KafkaMessageFormat.KAFKA_MESSAGE_FORMAT_JSON}
                  >
                    JSON
                  </MenuItem>
                  <MenuItem
                    value={KafkaMessageFormat.KAFKA_MESSAGE_FORMAT_PROTOBUF}
                  >
                    Protobuf
                  </MenuItem>
                  <MenuItem value={KafkaMessageFormat.KAFKA_MESSAGE_FORMAT_RAW}>
                    Raw
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>Key Handling</FormLabel>
                <Select
                  size='small'
                  value={kafkaKeyHandling}
                  onChange={(e) => {
                    const val = Object.values(KafkaKeyHandling).find(
                      (v) => v === e.target.value
                    );
                    if (val) {
                      props.setWizardState((s) => ({
                        ...s,
                        kafkaKeyHandling: val,
                      }));
                    }
                  }}
                >
                  <MenuItem value={KafkaKeyHandling.KAFKA_KEY_HANDLING_IGNORE}>
                    Ignore
                  </MenuItem>
                  <MenuItem value={KafkaKeyHandling.KAFKA_KEY_HANDLING_STORE}>
                    Store
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        )}

        {/* GCS Source Properties */}
        {!isS3 && !isKafka && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>GCS Source Properties</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Configure the GCS bucket and project for data ingestion.
                </Typography>
              </Stack>

              <FormTextField
                title='Bucket'
                placeholder='e.g. my-bucket'
                value={gcsBucket}
                validation={BUCKET_NAME_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    gcsBucket: e.target.value,
                  }))
                }
                dataTestId='gcs-bucket-input'
              />

              <FormControl fullWidth>
                <FormLabel>Project ID</FormLabel>
                <TextField
                  size='small'
                  placeholder='e.g. my-project-id'
                  value={gcsProjectId}
                  onChange={(e) =>
                    props.setWizardState((s) => ({
                      ...s,
                      gcsProjectId: e.target.value,
                    }))
                  }
                  data-testid='gcs-project-id-input'
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>Location</FormLabel>
                <Autocomplete
                  options={GCS_LOCATION_OPTIONS}
                  value={selectedGcsLocation}
                  onChange={(_event, newValue) =>
                    props.setWizardState((s) => ({
                      ...s,
                      gcsLocation: newValue?.value ?? '',
                    }))
                  }
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size='small'
                      placeholder='Search locations…'
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          'data-testid': 'gcs-location-input',
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </Box>
        )}

        {/* Data Format (S3/GCS only) */}
        {!isKafka && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>Data Format</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Select the file format of the source data.
                </Typography>
              </Stack>

              <FormControl fullWidth>
                <FormLabel>File Format</FormLabel>
                <Select
                  size='small'
                  value={fileFormat}
                  onChange={(e) =>
                    props.setWizardState((s) => ({
                      ...s,
                      fileFormat: e.target.value as FileFormat,
                    }))
                  }
                  data-testid='file-format-select'
                >
                  <MenuItem value={FileFormat.FILE_FORMAT_JSON}>JSON</MenuItem>
                  <MenuItem value={FileFormat.FILE_FORMAT_AVRO}>AVRO</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>
        )}
      </Stack>
    </StepContainer>
  );
}
